// Website prices and offers (approved 01/11/2026): Falcon Console → Website Prices sets what www.falconplm.com shows.
// Prices are per user per month in pounds (GBP), no VAT. An offer is a percentage off or a new price for chosen
// packages and billing, from a start date to an end date (UK dates, both days included). Ended offers come off the
// website by themselves. New agreements (Console → Agreements, Falcon HQ) are filled in from these prices; customers
// who already signed keep their price. Shared by the server (which stores them and writes them into the website) and
// the Console screen.

export const PRICE_PACKAGES = [
  { key: "plm", label: "Falcon PLM", short: "PLM" },
  { key: "both", label: "Falcon PLM + CRM", short: "PLM + CRM" },
  { key: "crm", label: "Falcon CRM", short: "CRM" },
] as const;
export type PricePackage = (typeof PRICE_PACKAGES)[number]["key"];
export const BILLINGS = [
  { key: "yearly", label: "Yearly", long: "Billed yearly" },
  { key: "monthly", label: "Monthly", long: "Billed monthly" },
] as const;
export type Billing = (typeof BILLINGS)[number]["key"];

export interface PackagePrice { yearly: number; monthly: number; label: string }
export interface Offer {
  id: string;
  /** Shown on the website, e.g. "Launch offer". */
  name: string;
  packages: PricePackage[];
  billing: Billing[];
  kind: "percent" | "price";
  /** kind "percent": 1 to 90. */
  percent: number | null;
  /** kind "price": the offer price for each chosen package and billing. */
  prices: Partial<Record<PricePackage, Partial<Record<Billing, number>>>>;
  /** YYYY-MM-DD, both included (UK dates). */
  start: string;
  end: string;
  /** How long a customer keeps the offer price: 0 = as long as they stay, otherwise the first N months. */
  months: number;
  /** Show a banner at the top of the website while the offer is live. */
  banner: boolean;
}
export interface Onboarding { price: number; offerPrice: number | null; offerName: string; offerEnds: string | null }
export interface PriceSettings {
  packages: Record<PricePackage, PackagePrice>;
  minUsers: number;
  offers: Offer[];
  onboarding: Onboarding;
}

/** The prices before anything is changed in the Console (the approved prices of 21/10/2026). */
export const DEFAULT_PRICES: PriceSettings = {
  packages: {
    plm: { yearly: 99, monthly: 118.8, label: "" },
    both: { yearly: 129, monthly: 154.8, label: "Best value" },
    crm: { yearly: 49, monthly: 58.8, label: "" },
  },
  minUsers: 5,
  offers: [],
  onboarding: { price: 1500, offerPrice: 1000, offerName: "Introductory offer", offerEnds: null },
};

export const MAX_PRICE = 10000;
export const MAX_OFFERS = 30;

const round2 = (n: number) => Math.round(n * 100) / 100;
const isDate = (s: unknown): s is string => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`));
const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(typeof v === "string" ? v.replace(/[£,\s]/g, "") : v);
  return Number.isFinite(n) ? round2(n) : null;
};
const text = (v: unknown, max: number) => String(v ?? "").replace(/[\r\n\t]+/g, " ").replace(/\s+/g, " ").trim().slice(0, max);

/** £99, £118.80, £1,500 */
export function gbp(n: number): string {
  return `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: Number.isInteger(round2(n)) ? 0 : 2, maximumFractionDigits: 2 })}`;
}
/** 30/11/2026 */
export const ukDay = (iso: string | null | undefined) => (isDate(iso) ? iso.split("-").reverse().join("/") : "");
/** Today's date in the UK (YYYY-MM-DD): offers start and end on UK days. */
export function ukToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}
export const packageLabel = (k: string) => PRICE_PACKAGES.find((p) => p.key === k)?.label || k;

/** Tidies what was typed (unknown keys dropped, numbers rounded to pence). Checking is validPrices'. */
export function normalizePrices(raw: any): PriceSettings {
  const r = raw && typeof raw === "object" ? raw : {};
  const packages = {} as Record<PricePackage, PackagePrice>;
  for (const p of PRICE_PACKAGES) {
    const x = r.packages?.[p.key] || {};
    const d = DEFAULT_PRICES.packages[p.key];
    packages[p.key] = { yearly: num(x.yearly) ?? d.yearly, monthly: num(x.monthly) ?? d.monthly, label: x.label === undefined ? d.label : text(x.label, 30) };
  }
  const offers: Offer[] = (Array.isArray(r.offers) ? r.offers : []).slice(0, MAX_OFFERS + 1).map((o: any, i: number) => {
    const pk = PRICE_PACKAGES.map((p) => p.key).filter((k) => Array.isArray(o?.packages) && o.packages.includes(k));
    const bl = BILLINGS.map((b) => b.key).filter((k) => Array.isArray(o?.billing) && o.billing.includes(k));
    const kind = o?.kind === "price" ? "price" : "percent";
    const prices: Offer["prices"] = {};
    if (kind === "price") for (const p of pk) for (const b of bl) {
      const v = num(o?.prices?.[p]?.[b]);
      if (v !== null) (prices[p] ||= {})[b] = v;
    }
    const months = Math.max(0, Math.min(120, Math.round(Number(o?.months) || 0)));
    return {
      id: text(o?.id, 40) || `offer-${i + 1}`, name: text(o?.name, 40), packages: pk, billing: bl, kind,
      percent: kind === "percent" ? num(o?.percent) : null, prices, start: String(o?.start || ""), end: String(o?.end || ""), months, banner: !!o?.banner,
    };
  });
  const ob = r.onboarding || {};
  const dob = DEFAULT_PRICES.onboarding;
  return {
    packages,
    minUsers: Math.round(Number(r.minUsers)) || DEFAULT_PRICES.minUsers,
    offers,
    onboarding: {
      price: num(ob.price) ?? dob.price,
      offerPrice: ob.offerPrice === undefined ? dob.offerPrice : num(ob.offerPrice),
      offerName: ob.offerName === undefined ? dob.offerName : text(ob.offerName, 40),
      offerEnds: isDate(ob.offerEnds) ? ob.offerEnds : null,
    },
  };
}

/** Problems with the prices, in plain words (empty when they can be saved). */
export function priceProblems(s: PriceSettings): Array<{ field: string; message: string }> {
  const out: Array<{ field: string; message: string }> = [];
  const bad = (field: string, message: string) => out.push({ field, message });
  for (const p of PRICE_PACKAGES) {
    const x = s.packages[p.key];
    for (const b of BILLINGS) {
      const v = x[b.key];
      if (!(v > 0) || v > MAX_PRICE) bad(`${p.key}.${b.key}`, `Enter a price between £0.01 and ${gbp(MAX_PRICE)} for ${p.label}, ${b.long.toLowerCase()}.`);
    }
  }
  if (!(s.minUsers >= 1 && s.minUsers <= 100)) bad("minUsers", "Minimum users must be between 1 and 100.");
  if (s.offers.length > MAX_OFFERS) bad("offers", `Keep at most ${MAX_OFFERS} offers. Delete some ended ones first.`);
  s.offers.forEach((o, i) => {
    const f = (k: string) => `offers.${i}.${k}`;
    const who = `Offer "${o.name || i + 1}"`;
    if (!o.name) bad(f("name"), `Offer ${i + 1} needs the name shown on the website.`);
    if (!o.packages.length) bad(f("packages"), `Choose at least one package for ${who}.`);
    if (!o.billing.length) bad(f("billing"), `Choose yearly, monthly or both for ${who}.`);
    if (!isDate(o.start)) bad(f("start"), `Choose the start date for ${who}.`);
    if (!isDate(o.end)) bad(f("end"), `Choose the end date for ${who}.`);
    else if (isDate(o.start) && o.end < o.start) bad(f("end"), `The end date of ${who} is before the start date.`);
    if (o.kind === "percent") {
      if (o.percent === null || !(o.percent >= 1 && o.percent <= 90)) bad(f("percent"), `The percentage off for ${who} must be between 1 and 90.`);
    } else {
      for (const p of o.packages) for (const b of o.billing) {
        const v = o.prices[p]?.[b];
        const normal = s.packages[p][b];
        const where = `${packageLabel(p)}, ${b}`;
        if (v === undefined || !(v > 0)) bad(f(`prices.${p}.${b}`), `Enter the offer price for ${where} in ${who}.`);
        else if (v >= normal) bad(f(`prices.${p}.${b}`), `In ${who}, the offer price for ${where} (${gbp(v)}) must be lower than the normal price (${gbp(normal)}).`);
      }
    }
    // One offer per package and billing at a time.
    s.offers.forEach((o2, j) => {
      if (j <= i || !isDate(o.start) || !isDate(o.end) || !isDate(o2.start) || !isDate(o2.end)) return;
      if (o.end < o2.start || o2.end < o.start) return;
      const both = o.packages.filter((p) => o2.packages.includes(p)).filter(() => o.billing.some((b) => o2.billing.includes(b)));
      if (both.length) bad(f("start"), `Only one offer per package at a time: ${who} and "${o2.name || j + 1}" both cover ${packageLabel(both[0]!)} on the same days.`);
    });
  });
  const ob = s.onboarding;
  if (!(ob.price >= 0 && ob.price <= 100000)) bad("onboarding.price", "Onboarding: enter a price between £0 and £100,000.");
  if (ob.offerPrice !== null && !(ob.offerPrice >= 0 && ob.offerPrice < ob.price)) bad("onboarding.offerPrice", "Onboarding: the offer price must be lower than the normal price (or leave it empty for no offer).");
  if (ob.offerPrice !== null && !ob.offerName) bad("onboarding.offerName", "Onboarding: enter the offer's name (for example Introductory offer).");
  return out;
}

export type OfferStatus = "scheduled" | "live" | "ended";
export function offerStatus(o: Offer, today: string): OfferStatus {
  return today < o.start ? "scheduled" : today > o.end ? "ended" : "live";
}
/** "20% off" or "New price" */
export function offerWhat(o: Offer): string {
  return o.kind === "percent" ? `${o.percent}% off` : "New price";
}
/** "for the first 12 months" / "for as long as they stay" (the website says "you stay"). */
export function offerLasts(o: Offer, who: "they" | "you" = "they"): string {
  if (o.months) return o.months === 1 ? "for the first month" : `for the first ${o.months} months`;
  return who === "you" ? "for as long as you stay" : "for as long as they stay";
}

export interface ShownPrice {
  /** The normal price. */
  normal: number;
  /** What is charged today (the offer price while an offer is live). */
  price: number;
  offer: { id: string; name: string; what: string; end: string; months: number } | null;
}

/** The price shown today for one package and billing. */
export function shownPrice(s: PriceSettings, pkg: PricePackage, billing: Billing, today: string): ShownPrice {
  const normal = s.packages[pkg][billing];
  const o = s.offers.find((x) => offerStatus(x, today) === "live" && x.packages.includes(pkg) && x.billing.includes(billing));
  if (!o) return { normal, price: normal, offer: null };
  const price = o.kind === "percent" ? round2(normal * (1 - (o.percent || 0) / 100)) : o.prices[pkg]?.[billing] ?? normal;
  if (!(price < normal)) return { normal, price: normal, offer: null };
  return { normal, price, offer: { id: o.id, name: o.name, what: offerWhat(o), end: o.end, months: o.months } };
}

/** The onboarding price shown today. */
export function shownOnboarding(s: PriceSettings, today: string): { normal: number; price: number; offerName: string; offerEnds: string | null } {
  const ob = s.onboarding;
  const live = ob.offerPrice !== null && ob.offerPrice < ob.price && (!ob.offerEnds || today <= ob.offerEnds);
  return live ? { normal: ob.price, price: ob.offerPrice!, offerName: ob.offerName, offerEnds: ob.offerEnds } : { normal: ob.price, price: ob.price, offerName: "", offerEnds: null };
}

/** How much more monthly billing is than yearly, when it is the same for all three packages (e.g. 20), otherwise null. */
export function monthlyUplift(s: PriceSettings): number | null {
  const ups = PRICE_PACKAGES.map((p) => Math.round((s.packages[p.key].monthly / s.packages[p.key].yearly - 1) * 1000) / 10);
  return ups.every((u) => u === ups[0]) && ups[0]! > 0 ? ups[0]! : null;
}

/** What changed between two saves, one line each, for the Activity log. */
export function priceChanges(before: PriceSettings, after: PriceSettings): Array<{ field: string; from: string; to: string }> {
  const out: Array<{ field: string; from: string; to: string }> = [];
  const add = (field: string, from: string, to: string) => { if (from !== to) out.push({ field, from, to }); };
  for (const p of PRICE_PACKAGES) {
    for (const b of BILLINGS) add([p.label, b.long.toLowerCase()].join(", "), gbp(before.packages[p.key][b.key]), gbp(after.packages[p.key][b.key]));
    add([p.label, "label"].join(", "), before.packages[p.key].label, after.packages[p.key].label);
  }
  add("Minimum users", String(before.minUsers), String(after.minUsers));
  const desc = (o: Offer) => `${o.name}: ${o.kind === "percent" ? `${o.percent}% off` : `new price ${o.packages.map((p) => o.billing.map((b) => `${PRICE_PACKAGES.find((x) => x.key === p)!.short} ${b} ${gbp(o.prices[p]?.[b] || 0)}`).join(", ")).join(", ")}`}`
    + ` · ${o.packages.map((p) => PRICE_PACKAGES.find((x) => x.key === p)!.short).join(", ")} · ${o.billing.join(" and ")} · ${ukDay(o.start)} to ${ukDay(o.end)} · ${offerLasts(o)}${o.banner ? " · banner" : ""}`;
  for (const o of after.offers) {
    const was = before.offers.find((x) => x.id === o.id);
    if (!was) add("Offer added", "", desc(o));
    else add(`Offer "${o.name}"`, desc(was), desc(o));
  }
  for (const o of before.offers) if (!after.offers.some((x) => x.id === o.id)) add("Offer deleted", desc(o), "");
  const ob = (x: Onboarding) => `${gbp(x.price)}${x.offerPrice !== null ? `, ${x.offerName} ${gbp(x.offerPrice)}${x.offerEnds ? ` until ${ukDay(x.offerEnds)}` : ""}` : ""}`;
  add("Onboarding", ob(before.onboarding), ob(after.onboarding));
  return out;
}
